import { z } from "zod";
import { ConversationItemType } from "@remi/enums/conversation-item/type";
import { OrganizationTypeSchema } from "@remi/enums/organization-type";
import { ParentRelationshipSchema } from "@remi/enums/parent-relationship";
import { PhoneCallStatusSchema } from "@remi/enums/phone-call-status";

import { EscrowHistorySchema } from "../escrow/history/history";
import { FinancingHistorySchema } from "../financing/history/history";
import { PandadocDocumentHistorySchema } from "../pandadoc-document/history/history";
import { TicketHistorySchema } from "../ticket/history/history";
import {
  ConversationItemNotificationTypeSchema,
  EscalationHistoryNotificationTypeSchema,
  EscrowHistoryNotificationTypeSchema,
  FinancingHistoryNotificationTypeSchema,
  PandadocDocumentHistoryNotificationTypeSchema,
  ProjectUpdatesNotificationTypeSchema,
  TicketHistoryNotificationTypeSchema,
} from "./type";

export const ConversationItemNotificationSchema = z
  .object({
    id: z.string(),
    orderDate: z.coerce.date(),
    parentRelationship: ParentRelationshipSchema.nullable(),
    conversation: z.object({
      id: z.string(),
      name: z.string(),
      organizations: z.array(z.object({ id: z.string(), type: OrganizationTypeSchema, name: z.string() })),
      persons: z.array(
        z.object({
          id: z.string(),
          organization: z.object({ id: z.string(), type: OrganizationTypeSchema, name: z.string() }),
        }),
      ),
      bid: z
        .object({
          id: z.number(),
          name: z.string(),
        })
        .nullable(),
      ticket: z
        .object({
          id: z.string(),
          title: z.string(),
          bid: z
            .object({
              id: z.number(),
              name: z.string(),
            })
            .nullable(),
        })
        .nullable(),
      task: z
        .object({
          id: z.string(),
        })
        .nullable(),
      escalation: z
        .object({
          id: z.string(),
          name: z.string(),
        })
        .nullable(),
    }),
    parent: z
      .object({
        id: z.string(),
        message: z
          .object({
            id: z.string(),
            text: z.string(),
          })
          .optional(),
      })
      .optional(),
  })
  .and(
    z.discriminatedUnion("type", [
      z.object({
        type: z.literal(ConversationItemType.MESSAGE),
        message: z.object({
          id: z.string(),
          text: z.string(),
        }),
        smsMessage: z.undefined().optional(),
        phoneCall: z.undefined().optional(),
      }),
      z.object({
        type: z.literal(ConversationItemType.SMS_MESSAGE),
        smsMessage: z.object({
          id: z.string(),
          text: z.string(),
        }),
        message: z.undefined().optional(),
        phoneCall: z.undefined().optional(),
      }),
      z.object({
        type: z.literal(ConversationItemType.PHONE_CALL),
        phoneCall: z.object({
          id: z.string(),
          status: PhoneCallStatusSchema,
        }),
        message: z.undefined().optional(),
        smsMessage: z.undefined().optional(),
      }),
    ]),
  );

export const TicketHistoryNotificationSchema = TicketHistorySchema.and(
  z.object({
    ticket: z.object({
      id: z.string(),
      title: z.string(),
      bid: z
        .object({
          id: z.number(),
          name: z.string(),
        })
        .nullable(),
    }),
  }),
);

const SalesDetailsSchema = z.object({
  bid: z.object({
    id: z.number(),
    name: z.string(),
  }),
});

export const EscrowHistoryNotificationSchema = EscrowHistorySchema.and(SalesDetailsSchema);

export const FinancingHistoryNotificationSchema = FinancingHistorySchema.and(SalesDetailsSchema);

export const PandadocDocumentHistoryNotificationSchema = PandadocDocumentHistorySchema.and(SalesDetailsSchema);

const EscalationHistoryNotificationListSchema = z.object({
  id: z.string(),
  // Inlined (not imported from @remi/types-enums) — the structural boundary forbids
  // a @remi/types → @remi/types-enums edge. Mirrors EscalationHistoryNotificationFieldValues.
  field: z.enum(["resolutionStage", "riskTierOverride", "assigneeId"]),
  changedFrom: z.string().nullable(),
  changedTo: z.string().nullable(),
  changedById: z.string(),
  escalationId: z.string(),
  createdAt: z.coerce.date(),
  escalationName: z.string(),
  homeownerName: z.string().nullable(),
  changedFromName: z.string().nullable(),
  changedToName: z.string().nullable(),
});

const baseNotificationSchema = z.object({
  ticketHistory: z.undefined().optional(),
  escalationHistory: z.undefined().optional(),
  escrowHistory: z.undefined().optional(),
  financingHistory: z.undefined().optional(),
  pandadocDocumentHistory: z.undefined().optional(),
  conversationItem: z.undefined().optional(),
});

export const NotificationListItemSchema = z
  .object({
    id: z.string(),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
    personId: z.string(),
    actor: z.object({
      id: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      contact: z.object({ name: z.string() }),
    }),
    readAt: z.coerce.date().nullable().catch(null),
    deletedAt: z.coerce.date().nullable().catch(null),
    app: z.boolean(),
    sms: z.boolean(),
    email: z.boolean(),
    toast: z.boolean(),
    slack: z.boolean(),
  })
  .and(
    z.discriminatedUnion("type", [
      baseNotificationSchema.extend({
        type: ConversationItemNotificationTypeSchema,
        conversationItem: ConversationItemNotificationSchema,
      }),
      baseNotificationSchema.extend({
        type: TicketHistoryNotificationTypeSchema,
        ticketHistory: TicketHistoryNotificationSchema,
      }),
      baseNotificationSchema.extend({
        type: EscrowHistoryNotificationTypeSchema,
        escrowHistory: EscrowHistoryNotificationSchema,
      }),
      baseNotificationSchema.extend({
        type: FinancingHistoryNotificationTypeSchema,
        financingHistory: FinancingHistoryNotificationSchema,
      }),
      baseNotificationSchema.extend({
        type: PandadocDocumentHistoryNotificationTypeSchema,
        pandadocDocumentHistory: PandadocDocumentHistoryNotificationSchema,
      }),
      baseNotificationSchema.extend({
        type: EscalationHistoryNotificationTypeSchema,
        escalationHistory: EscalationHistoryNotificationListSchema,
      }),
      baseNotificationSchema.extend({
        type: ProjectUpdatesNotificationTypeSchema,
        taskId: z.string().nullable(),
      }),
    ]),
  );

export type NotificationListItem = z.infer<typeof NotificationListItemSchema>;

export type GroupedNotificationListItem = {
  id: string;
  notifications: NotificationListItem[];
};
